import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Typography,
  Popover,
  TextField,
  MenuItem,
  Divider,
  ListItemText,
  Tooltip,
} from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import AddIcon from "@material-ui/icons/Add";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { normalizeBucket, NO_BUCKET } from "../modules/playEngineAssetUtils";

const LABEL_BASE_STYLE = {
  display: "inline-block",
  padding: "4px 10px",
  borderRadius: "4px",
  backgroundColor: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(176, 190, 197, 0.45)",
};

const POPOVER_PAPER_STYLE = {
  backgroundColor: "#383838",
  padding: "8px",
  minWidth: "240px",
  maxWidth: "340px",
};

const ITEM_SELECTED_BG = "rgba(126, 155, 214, 0.28)";

/** Friendlier labels for the per-type count breakdown. */
const TYPE_LABELS = {
  audio: "sound",
  "bitmap-font": "bitmap",
};
const typeLabel = (type) => TYPE_LABELS[type] || type;

function findScrollParent(node) {
  let el = node?.parentElement;
  while (el) {
    const { overflowY } = window.getComputedStyle(el);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      el.scrollHeight > el.clientHeight
    ) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

/**
 * Clickable "Bucket: …" label that opens a popover listing the buckets the
 * company already uses, plus an inline "create new" affordance.
 *
 * Buckets are just strings; selecting one (or creating a new name) calls
 * `onBucketChange` with the normalized lowercase value (or NO_BUCKET).
 *
 * Counts can be supplied two ways:
 *  - `bucketCounts`:       bucket -> number (a single total; used by the Assets tab)
 *  - `bucketCountDetails`: bucket -> { total, parts: [{ type, count }] }
 *                          (per-type breakdown; used by Version Settings so the
 *                          count reflects only the component's dependency types)
 * When both are given, `bucketCountDetails` wins.
 *
 * @param {object}   props
 * @param {string}   props.bucket               current bucket (may be "nobucket"/"")
 * @param {string[]} [props.buckets]            available bucket names to choose from
 * @param {object}   [props.bucketCounts]       bucket -> total count
 * @param {object}   [props.bucketCountDetails] bucket -> { total, parts:[{type,count}] }
 * @param {boolean}  [props.disabled]
 * @param {function} props.onBucketChange       (value: string) => void
 * @param {object}   [props.style]              extra style for the label
 */
export default function BucketPicker({
  bucket,
  buckets = [],
  bucketCounts = null,
  bucketCountDetails = null,
  disabled = false,
  onBucketChange,
  style,
}) {
  const current = normalizeBucket(bucket);
  const [anchorEl, setAnchorEl] = useState(null);
  const [query, setQuery] = useState("");
  const [noAssetsExpanded, setNoAssetsExpanded] = useState(false);
  const scrollParentRef = useRef({ el: null, top: 0 });

  const resolveEntry = (name) => {
    const detail = bucketCountDetails ? bucketCountDetails[name] : null;
    if (detail) return { count: detail.total || 0, parts: detail.parts || null };
    if (bucketCounts) return { count: bucketCounts[name] || 0, parts: null };
    return { count: null, parts: null };
  };

  const options = useMemo(() => {
    const set = new Set();
    (buckets || []).forEach((b) => {
      const n = normalizeBucket(b);
      if (n !== NO_BUCKET) set.add(n);
    });
    // Keep the currently-selected bucket visible even if no media uses it yet.
    if (current !== NO_BUCKET) set.add(current);
    const list = Array.from(set).map((name) => ({ name, ...resolveEntry(name) }));
    list.sort((a, b) => {
      // When counts are known, sink empty (count 0) buckets to the bottom.
      if (a.count != null && b.count != null) {
        if (b.count !== a.count) return b.count - a.count;
      }
      return a.name.localeCompare(b.name);
    });
    return list;
  }, [buckets, current, bucketCounts, bucketCountDetails]);

  const trimmedQuery = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!trimmedQuery) return options;
    return options.filter((o) => o.name.includes(trimmedQuery));
  }, [options, trimmedQuery]);

  const { withAssets, noAssets } = useMemo(() => {
    const withAssets = [];
    const noAssets = [];
    filtered.forEach((o) => {
      if (o.count === 0) noAssets.push(o);
      else withAssets.push(o);
    });
    return { withAssets, noAssets };
  }, [filtered]);

  const showNoBucket = !trimmedQuery || "no bucket".includes(trimmedQuery) || NO_BUCKET.includes(trimmedQuery);
  const canCreate = trimmedQuery && !options.some((o) => o.name === trimmedQuery);
  const selectedInNoAssets = noAssets.some((o) => o.name === current);

  useEffect(() => {
    if (!anchorEl) {
      setNoAssetsExpanded(false);
      return;
    }
    setNoAssetsExpanded(selectedInNoAssets);
  }, [anchorEl, selectedInNoAssets]);

  function restoreScrollAfterPopover() {
    const { el, top } = scrollParentRef.current || {};
    if (!el) return;
    const apply = () => {
      el.scrollTop = top;
    };
    requestAnimationFrame(() => {
      apply();
      requestAnimationFrame(apply);
    });
  }

  function openPopover(e) {
    if (disabled) return;
    const scrollParent = findScrollParent(e.currentTarget);
    scrollParentRef.current = scrollParent
      ? { el: scrollParent, top: scrollParent.scrollTop }
      : { el: null, top: 0 };
    setQuery("");
    setAnchorEl(e.currentTarget);
  }
  function closePopover() {
    setAnchorEl(null);
    setQuery("");
    restoreScrollAfterPopover();
  }
  function select(value) {
    onBucketChange(normalizeBucket(value));
    closePopover();
  }

  return (
    <>
      <Typography
        variant="caption"
        style={{
          ...LABEL_BASE_STYLE,
          color: current === NO_BUCKET ? "#b0bec5" : "#e3eaf0",
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.65 : 1,
          ...style,
        }}
        onClick={openPopover}
      >
        Bucket:{" "}
        <span style={{ fontWeight: 600 }}>
          {current === NO_BUCKET ? "No Bucket" : current}
        </span>
      </Typography>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePopover}
        disableRestoreFocus
        TransitionProps={{ onExited: restoreScrollAfterPopover }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ style: POPOVER_PAPER_STYLE }}
      >
        <TextField
          autoFocus
          size="small"
          fullWidth
          placeholder="Filter or type a new bucket…"
          value={query}
          disabled={disabled}
          InputProps={{ style: { color: "#fff", fontSize: "13px" } }}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "Enter" && canCreate) select(trimmedQuery);
            else if (e.key === "Escape") closePopover();
          }}
          style={{ marginBottom: 6 }}
        />

        <div style={{ maxHeight: 240, overflowY: "auto", margin: "0 -4px" }}>
          {showNoBucket && (
            <BucketMenuItem
              label="No Bucket"
              italic
              selected={current === NO_BUCKET}
              {...resolveEntry(NO_BUCKET)}
              onClick={() => select(NO_BUCKET)}
            />
          )}

          {withAssets.map((o) => (
            <BucketMenuItem
              key={o.name}
              label={o.name}
              selected={o.name === current}
              count={o.count}
              parts={o.parts}
              onClick={() => select(o.name)}
            />
          ))}

          {noAssets.length > 0 && (
            <NoAssetsBucketDrawer
              expanded={noAssetsExpanded}
              count={noAssets.length}
              onToggle={() => setNoAssetsExpanded((v) => !v)}
            >
              {noAssets.map((o) => (
                <BucketMenuItem
                  key={o.name}
                  label={o.name}
                  selected={o.name === current}
                  count={o.count}
                  parts={o.parts}
                  dim
                  nested
                  onClick={() => select(o.name)}
                />
              ))}
            </NoAssetsBucketDrawer>
          )}

          {!showNoBucket && withAssets.length === 0 && noAssets.length === 0 && !canCreate && (
            <Typography
              variant="caption"
              style={{ color: "#888", display: "block", padding: "6px 12px" }}
            >
              No matching buckets
            </Typography>
          )}
        </div>

        {canCreate && (
          <>
            <Divider style={{ backgroundColor: "rgba(255,255,255,0.12)", margin: "6px 0" }} />
            <MenuItem
              dense
              onClick={() => select(trimmedQuery)}
              style={{ color: "#9ec1ff", paddingLeft: 12 }}
            >
              <AddIcon fontSize="small" style={{ marginRight: 8 }} />
              <ListItemText
                primary={`Create "${trimmedQuery}"`}
                primaryTypographyProps={{ style: { fontSize: 13 } }}
              />
            </MenuItem>
          </>
        )}
      </Popover>
    </>
  );
}

function NoAssetsBucketDrawer({ expanded, count, onToggle, children }) {
  return (
    <div
      style={{
        marginTop: 8,
        marginBottom: 2,
        borderRadius: 4,
        backgroundColor: "rgba(0, 0, 0, 0.22)",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.03)",
        opacity: 0.72,
        overflow: "hidden",
      }}
    >
      <MenuItem
        dense
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        style={{
          color: "#777",
          paddingLeft: 8,
          paddingRight: 8,
          minHeight: 28,
          backgroundColor: "transparent",
        }}
      >
        {expanded ? (
          <ExpandLessIcon fontSize="small" style={{ marginRight: 4, color: "#666", flexShrink: 0 }} />
        ) : (
          <ExpandMoreIcon fontSize="small" style={{ marginRight: 4, color: "#666", flexShrink: 0 }} />
        )}
        <Typography
          variant="caption"
          style={{
            color: "#666",
            fontSize: 10,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            userSelect: "none",
            flex: 1,
          }}
        >
          no asset buckets
        </Typography>
        <Typography
          variant="caption"
          style={{
            color: "#555",
            fontSize: 10,
            fontVariantNumeric: "tabular-nums",
            userSelect: "none",
          }}
        >
          {count}
        </Typography>
      </MenuItem>
      {expanded && <div style={{ padding: "0 0 2px" }}>{children}</div>}
    </div>
  );
}

function BucketMenuItem({ label, selected, italic, dim, nested, count, parts, onClick }) {
  const countColor = count ? "#cfcf8a" : "#888";
  return (
    <MenuItem
      dense
      selected={selected}
      onClick={onClick}
      style={{
        color: italic ? "#b0bec5" : "#eee",
        fontStyle: italic ? "italic" : "normal",
        opacity: dim ? 0.55 : 1,
        paddingLeft: nested ? 8 : 12,
        backgroundColor: selected ? ITEM_SELECTED_BG : "transparent",
      }}
    >
      <CheckIcon
        fontSize="small"
        style={{ marginRight: 8, flexShrink: 0, opacity: selected ? 1 : 0, color: "#9ec1ff" }}
      />
      <Tooltip title={label} enterDelay={400} placement="top">
        <ListItemText
          primary={label}
          style={{ minWidth: 0, marginRight: 8, flex: 1 }}
          primaryTypographyProps={{
            noWrap: true,
            style: { fontSize: 13, overflow: "hidden", textOverflow: "ellipsis" },
          }}
        />
      </Tooltip>
      {parts && parts.length > 0 ? (
        <Typography
          variant="caption"
          style={{
            marginLeft: "auto",
            paddingLeft: 8,
            flexShrink: 0,
            whiteSpace: "nowrap",
            color: countColor,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {parts.map((p) => `${typeLabel(p.type)}: ${p.count}`).join("  ·  ")}
        </Typography>
      ) : count != null ? (
        <Typography
          variant="caption"
          style={{
            marginLeft: "auto",
            paddingLeft: 8,
            minWidth: 18,
            flexShrink: 0,
            textAlign: "right",
            color: countColor,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {count}
        </Typography>
      ) : null}
    </MenuItem>
  );
}
